/**
 * Reconnection policy of the realtime channel (socket v2, R2).
 * Pure functions and constants: no Angular, no socket.io, so they are
 * trivially testable and the service stays about lifecycle only.
 */

export const RECONNECT_BASE_DELAY_MS = 1000;
export const RECONNECT_MAX_DELAY_MS = 30000;
/** After this many consecutive failed attempts the service stops; the next login (setToken) restarts it. */
export const RECONNECT_MAX_ATTEMPTS = 10;

/** Exponential backoff: 1 s, 2 s, 4 s, ... capped at 30 s. `attempt` is 1-based. */
export function reconnectDelayMs(attempt: number): number {
  const exponent = Math.max(0, Math.floor(attempt) - 1);
  return Math.min(RECONNECT_BASE_DELAY_MS * Math.pow(2, exponent), RECONNECT_MAX_DELAY_MS);
}

/**
 * Reasons the server sends as the `connect_error` message when it rejects a
 * handshake (`socket-api/src/auth/types.ts`, documented in `docs/asyncapi.yaml`).
 */
export const HANDSHAKE_REJECT_REASONS: ReadonlySet<string> = new Set([
  'missing_credential',
  'malformed',
  'unknown_issuer',
  'bad_signature',
  'expired',
  'ttl_too_long',
  'scope_not_allowed',
  'bad_subject',
  'legacy_token_lifetime',
  'unmapped_role',
  'revoked',
  'rate_limited',
]);

/** The error-like object socket.io-client 3.x hands to `connect_error`. */
export interface ConnectErrorLike {
  message?: string;
  /** engine.io sets `TransportError` on transport failures. */
  type?: string;
}

/**
 * `connect_error` in socket.io-client 3.1 carries two unrelated things:
 * - a handshake rejected by the server middleware, whose `message` is the
 *   reason (snake_case). The credential is final: only a fresh one can help;
 * - a transport failure (engine.io `TransportError`, the manager's "timeout"),
 *   which socket.io retries on its own with the same credential.
 * Only the first needs a re-mint. Unknown snake_case reasons count as
 * rejections so a reason added server-side does not revive the old loop.
 */
export function isHandshakeRejection(error: ConnectErrorLike | null | undefined): boolean {
  if (!error || error.type === 'TransportError') {
    return false;
  }
  const message = typeof error.message === 'string' ? error.message : '';
  if (message === 'timeout') {
    return false;
  }
  return HANDSHAKE_REJECT_REASONS.has(message) || /^[a-z]+(_[a-z]+)*$/.test(message);
}
